import React from 'react';
import { Trash2, Search, Plus, X, Edit2, Eye, EyeOff, ExternalLink } from 'lucide-react';

const CATEGORY_LABELS = {
  "barnums-tentes": "Barnums & Tentes",
  "mobilier-deco": "Mobilier - Vaisselle",
  "materiel-accessoires": "Accessoires - Matériel",
  "gonflables-jeux": "Gonflables - Jeux",
  "restauration": "Restauration - Buvette",
  "pack-tout-inclus": "PACK TOUT INCLUS",
};

const SUBCATEGORIES_BY_CATEGORY = {
  "barnums-tentes": [
    { slug: "petit-barnum", name: "Petit BARNUM - 18 à 40 assises" },
    { slug: "moyen-barnum", name: "Moyen BARNUM - 48 à 84 assises" },
    { slug: "grand-barnum", name: "Grand BARNUM - 96 à 120 assis" },
    { slug: "geant-barnum", name: "Géant BARNUM - 128 à 156 assis" },
    { slug: "tentes-pliantes", name: "TENTES PLIANTES - raccordables" },
    { slug: "parquets", name: "PLANCHER DE RÉCEPTION" },
    { slug: "chapiteaux", name: "Chapiteaux" },
  ],
  "mobilier-deco": [
    { slug: "chaises", name: "Chaises et bancs" },
    { slug: "tables", name: "Tables et cocktails" },
    { slug: "vaisselle", name: "Vaisselle" },
    { slug: "decoration", name: "Décoration" },
  ],
  "materiel-accessoires": [
    { slug: "chauffage", name: "Chauffage" },
    { slug: "eclairage", name: "Éclairage" },
    { slug: "sonorisation", name: "Sons et Ambiance" },
    { slug: "groupe-electrogene", name: "Groupe électrogène" },
    { slug: "remorques-frigo", name: "Remorques Frigo" },
  ],
  "gonflables-jeux": [
    { slug: "grandes-structures", name: "Grandes Structures Gonflables" },
    { slug: "petits-chateaux", name: "Petits Châteaux Gonflables" },
    { slug: "jeux", name: "Accessoires & Jeux" },
  ],
  "restauration": [
    { slug: "animations-gourmandes", name: "Animations gourmandes" },
    { slug: "tireuses-biere", name: "Tireuses à Bière" },
    { slug: "remorques-frigo", name: "Remorques Frigorifiques" },
  ],
  "pack-tout-inclus": [
    { slug: "packs", name: "Nos Packs" },
  ],
};

const StatusBadge = ({ status }) => {
  const isAvailable = status !== 'Indisponible';
  return (
    <span className={"inline-flex px-2.5 py-1 rounded-full text-xs font-semibold " + (isAvailable ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600")}>
      {status || 'Disponible'}
    </span>
  );
};

const ProductForm = ({ form, setForm, editing, onSave, onCancel }) => {
  const subcatOptions = SUBCATEGORIES_BY_CATEGORY[form.category] || [];
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200" data-testid="product-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{editing ? 'Modifier le produit' : 'Nouveau produit'}</h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit</label>
          <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="product-name-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
          <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value, subcategory: '' }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="product-category-select">
            <option value="">Sélectionner...</option>
            {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
              <option key={slug} value={slug}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
          <select value={form.subcategory} onChange={(e) => setForm(f => ({ ...f, subcategory: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="product-subcategory-select">
            <option value="">Sélectionner...</option>
            {subcatOptions.map((sub) => (
              <option key={sub.slug} value={sub.slug}>{sub.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prix Jour</label>
          <input type="text" value={form.priceDay} onChange={(e) => setForm(f => ({ ...f, priceDay: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" placeholder="ex: 10€" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prix W-E</label>
          <input type="text" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" placeholder="ex: 90€/WE" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input type="text" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
          <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none">
            <option value="Disponible">Disponible</option>
            <option value="Indisponible">Indisponible</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">URL Image</label>
          <input type="text" value={form.image} onChange={(e) => setForm(f => ({ ...f, image: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" placeholder="https://..." />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lien Booqable (réservation)</label>
          <input type="text" value={form.booqableUrl || ''} onChange={(e) => setForm(f => ({ ...f, booqableUrl: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" placeholder="https://barnum-evenement.booqableshop.com/..." />
        </div>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onSave}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium" data-testid="product-save-btn">
          {editing ? 'Enregistrer' : 'Créer le produit'}
        </button>
        <button onClick={onCancel}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
          Annuler
        </button>
      </div>
    </div>
  );
};

const ProductsTab = ({
  products, searchQuery, setSearchQuery, categoryFilter, setCategoryFilter,
  showProductForm, setShowProductForm, editingProduct, setEditingProduct,
  productForm, setProductForm, onCreateProduct, onUpdateProduct, onDeleteProduct,
  onToggleStatus, onStartEdit,
}) => {
  const filteredProducts = products.filter(p => {
    const matchSearch = !searchQuery.trim() || p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = !categoryFilter || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const uniqueCategories = [...new Set(products.map(p => p.category))];
  const defaultForm = { name: '', description: '', price: '', priceDay: '', caution: '', category: '', subcategory: '', image: '', status: 'Disponible', booqableUrl: '' };

  const handleCancel = () => {
    setShowProductForm(false);
    setEditingProduct(null);
  };

  const handleAdd = () => {
    setShowProductForm(true);
    setEditingProduct(null);
    setProductForm(defaultForm);
  };

  const getSubcatLabel = (cat, subcat) => {
    const subs = SUBCATEGORIES_BY_CATEGORY[cat];
    if (!subs) return subcat;
    const found = subs.find(s => s.slug === subcat);
    return found ? found.name : subcat;
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Produits</h2>
          <p className="text-sm text-gray-500 mt-1">{products.length} produits &bull; {uniqueCategories.length} catégories</p>
        </div>
        <button onClick={handleAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Ajouter un produit
        </button>
      </div>

      {showProductForm && (
        <ProductForm
          form={productForm}
          setForm={setProductForm}
          editing={!!editingProduct}
          onSave={editingProduct ? onUpdateProduct : onCreateProduct}
          onCancel={handleCancel}
        />
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Rechercher un produit..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white"
            data-testid="admin-product-search" />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white min-w-[200px]"
          data-testid="admin-category-filter">
          <option value="">Toutes les catégories</option>
          {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
            <option key={slug} value={slug}>{label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-products-list">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">Image</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">Prix W-E</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-28">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-20">Lien</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50 transition-colors" data-testid={"admin-product-row-" + product.id}>
                  <td className="px-4 py-3">
                    <img src={product.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100" loading="lazy" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm text-gray-900">{product.name}</div>
                    <div className="text-xs text-gray-400">{getSubcatLabel(product.category, product.subcategory)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{CATEGORY_LABELS[product.category] || product.category}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium text-gray-700">{product.price || '-'}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={product.status} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {product.booqableUrl ? (
                      <a href={product.booqableUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                        <ExternalLink className="w-4 h-4 inline" />
                      </a>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => onStartEdit(product)}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Modifier"
                        data-testid={"edit-product-" + product.id}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => onToggleStatus(product)}
                        className={"p-1.5 rounded transition-colors " + (product.status === 'Indisponible' ? "text-green-500 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100")}
                        title={product.status === 'Indisponible' ? 'Rendre disponible' : 'Rendre indisponible'}
                        data-testid={"toggle-status-" + product.id}>
                        {product.status === 'Indisponible' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => onDeleteProduct(product.id)}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded transition-colors" title="Supprimer"
                        data-testid={"delete-product-" + product.id}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredProducts.length === 0 && (
          <div className="p-8 text-center text-gray-500">Aucun produit trouvé.</div>
        )}
      </div>
    </>
  );
};

export default ProductsTab;
